import styled from "styled-components";
import axios from "axios";
import BreadcrumbTopSection from "../../commonComponents/BreadcrumbTopSection";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectMarket } from "../../store/slices/globalConfigSlice";
import { Banner, BREAKPOINTS, COLORS, PageWidth, AccordionMenu, SearchBar, Size, LoadingIndicator, SystemIcons, ComponentS, ComponentTextStyle } from "@laerdal/life-react-components";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import ProductApi from "../../services/api/ProductApi";
import DownloadsAccordionChild from "./DownloadsAccordionChild";
import { ProductLineDetailsDto } from "../../model/dto/productLineDetailsDto";
import posthog from "posthog-js";
import { PageWidthFull } from "../../commonComponents/PageWidthFull";
import { marketList } from "../../model/constants/marketContants";



const DownloadsPageContainer = styled.div`
  margin: 0 0 32px 0;
  gap: 32px;
  display: flex;
  flex-direction: column;
`;

const ContactContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
`;

const UnderlinedTextWrapper = styled.span`
  text-decoration: underline;
  cursor: pointer;
`;

const UnderlinedText = styled(ComponentS)`
  text-decoration: underline;
`;

const PageContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 32px;
`;

const PageTitle = styled.div`
  ${BREAKPOINTS.LARGE}{
    font-size: 40px;
  }

  font-size: 32px;
  display: flex;
  flex-direction: column;
  justify-content: start;
  flex-shrink: 0;
  color: #1D1D1F;

  text-align: start;
  font-style: normal;
  font-weight: 600;
  line-height: 44px;
`;

const DownloadsAccordionMenu = styled(AccordionMenu)`
  border-radius: 8px;
  border: 1px solid ${COLORS.neutral_200};

  & > div > div.active {
    background-color: ${COLORS.primary_100};
  }

  & > div.medium > div:nth-child(2) {
    padding: 0;
    background-color: ${COLORS.neutral_20};
  }
`


const DownloadsPage = () => {
  const { t } = useTranslation("landingPage");
  const { categoryLink, productLink } = useParams<{ categoryLink: string, productLink: string }>();
  const currentMarket = useSelector(selectMarket);
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[] | undefined>(undefined);
  const [items, setItems] = useState<any[]>([]);
  const [filteredItems, setFilteredItems] = useState<any[]>([]);
  const searchTerm = searchParams.get('search') || '';
  const [loading, setLoading] = useState<boolean>(true);
  const [productLineName, setProductLineName] = useState<string | undefined>(undefined);
  const [productLineLoading, setProductLineLoading] = useState<boolean>(true);
  const [productLine, setProductLine] = useState<ProductLineDetailsDto | undefined>(undefined);

  // Search function to filter items based on search term
  const handleSearch = (term: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (term.trim() === '') {
        next.delete('search');
      } else {
        next.set('search', term);
      }
      return next;
    }, { replace: true });

    if (term.trim() === "") {
      setFilteredItems(items);
      posthog?.capture('SUPP-DownloadsPage-SearchCleared', {
        page: 'DownloadsPage',
        productLink: productLink
      });
    } else {
      const filtered = items.filter((item) =>
        item.name.toLowerCase().includes(term.toLowerCase())
      );
      setFilteredItems(filtered);
      posthog?.capture('SUPP-DownloadsPage-Search', {
        page: 'DownloadsPage',
        searchTerm: term,
        resultsCount: filtered.length,
        productLink: productLink
      });
    }
  };

  useEffect(() => {
    // Reset state when productLineCode changes
    setItems([]);
    setFilteredItems([]);
    setSelectedId(null);
    setSelectedIds(undefined);
    setProductLineName(undefined);
    setLoading(true);

    ProductApi.GetProductLinesWithDocuments().then((productLines) => {
      const mappedItems = (productLines || []).map((pl: any) => ({
        id: pl.code,
        title: pl.name,
        code: pl.code,
        name: pl.name,
        loaded: false,
        loading: false
      })).sort((a, b) => a.title.localeCompare(b.title));

      if (productLink) {
        // ProductApi.GetProductLineCodeByAgileCode(productLink).then((code) => {
        const code = productLink;
        if (code) {
          const selectedItem = mappedItems.find(item => item.code.toLowerCase() === code.toLowerCase());
          const selectedItemWithChild = {
            ...selectedItem,
            children: <DownloadsAccordionChild isSelected={true} code={code} name={selectedItem?.name} />
          };

          setProductLineName(selectedItem?.name || "");
          setItems([selectedItemWithChild]);
          setFilteredItems([selectedItemWithChild]);
          setSelectedIds([selectedItem?.code]);
          setLoading(false);
        }
        setLoading(false);
        //)
      }
      else {
        setItems(mappedItems);
        const initialSearch = searchParams.get('search') || '';
        if (initialSearch) {
          const filtered = mappedItems.filter((item: any) =>
            item.name.toLowerCase().includes(initialSearch.toLowerCase())
          );
          setFilteredItems(filtered);
        } else {
          setFilteredItems(mappedItems);
        }
        setLoading(false);
      }
    });
  }, [productLink]); // Add productLineSupportCode to dependency array

  useEffect(() => {
    setFilteredItems((prevItems) =>
      prevItems.map((item: any) => ({
        ...item,
        children: <DownloadsAccordionChild isSelected={item.id === selectedId} code={item.code} name={item.name} />
      }))
    );
  }, [selectedId]);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    setProductLineLoading(true);
    setProductLine(undefined);

    if (categoryLink && productLink) {
      ProductApi
        .GetProductLine(productLink, marketList[currentMarket]?.locale, signal, true)
        .then((data) => {
          setProductLineLoading(false);
          setProductLine(data);

        })
        .catch((error) => {
          if (axios.isCancel(error)) return;
        })
    }

    return () => {
      controller.abort();
    };
  }, [categoryLink, productLink, currentMarket]);

  useEffect(() => {
    posthog?.capture('SUPP-DownloadsPage-PageVisit', {
      page: 'DownloadsPage',
      categoryLink: categoryLink,
      productLink: productLink,
      hasProductFilter: !!productLink
    });
  }, [categoryLink, productLink]);


  return (
    <>

      <PageWidthFull $useMaxWidth={true} $maxWidth={1024} $unsetMargin={true}>
        <PageContainer>
          {!productLink &&
            <BreadcrumbTopSection
              setPadding={false}
              breadcrumb={[{
                text: t('Home'),
                link: `/${currentMarket}`
              }, {
                text: t("Downloads")
              }]}
            />
          }

          {!!productLink &&
            <BreadcrumbTopSection
              setPadding={false}
              breadcrumb={[{
                text: t('Home'),
                link: `/${currentMarket}`
              }, {
                text: t(productLine?.categoryName ?? ''),
                link: !!currentMarket ? `/${currentMarket}/category/${categoryLink}` : `/category/${categoryLink}`
              }, {
                text: productLine?.displayName ?? '',
                link: !!currentMarket ? `/${currentMarket}/category/${categoryLink}/product/${productLink}` : `/category/${categoryLink}/product/${productLink}`
              }, {
                text: t("Downloads")
              }]}
            />
          }

          <PageTitle>{t("Downloads")}</PageTitle>

          {!productLink &&
            <Banner
              type="warning"
              fullWidth={true}
              icon={<SystemIcons.TechnicalWarning />}
            >
              {t('Laerdal no longer supports software for Windows XP as Microsoft does not provide technical support or security updates for this operating system.')}
            </Banner>
          }
          <DownloadsPageContainer>

            {loading && <LoadingIndicator />}



            {!loading &&
              <>
                {!productLink &&
                  <>
                    <SearchBar
                      searchTerm={searchTerm || productLineName}
                      id="DownloadsSearchBar"
                      enterSearch={(e) => {
                        if (e && e.target && typeof e.target.value === 'string') {
                          handleSearch(e.target.value);
                        }
                      }}
                      setSearchTerm={handleSearch}
                      removeSearch={() => handleSearch("")}
                      size={Size.Small}
                      placeholder={t('Search for Product name')}
                    />
                    <ContactContainer>
                      <ComponentS textStyle={ComponentTextStyle.Regular}>
                        {t("Can not find what you're looking for?")}
                      </ComponentS>
                      <UnderlinedTextWrapper
                        onClick={() => {
                          posthog?.capture('SUPP-DownloadsPage-ContactUsClicked', {
                            page: 'DownloadsPage',
                            link: 'https://laerdal.com/support/customer-service/contact-us/'
                          });
                          window.open('https://laerdal.com/support/customer-service/contact-us/', '_blank');
                        }}>
                        <UnderlinedText textStyle={ComponentTextStyle.Bold} color={COLORS.primary_700}>
                          {t("We can help.")}
                        </UnderlinedText>
                      </UnderlinedTextWrapper>
                    </ContactContainer>
                  </>
                }
                <DownloadsAccordionMenu
                  multipleActive={false}
                  selectedItems={selectedIds}
                  onSelect={(code) => {
                    setSelectedId(code);
                    const selectedItem = filteredItems.find(item => item.id === code);
                    posthog?.capture('SUPP-DownloadsPage-ProductLineSelected', {
                      page: 'DownloadsPage',
                      productLineCode: code,
                      productLineName: selectedItem?.name
                    });
                  }}
                  items={filteredItems}
                />
              </>
            }
          </DownloadsPageContainer>
        </PageContainer>
      </PageWidthFull>
    </>);
};

export default DownloadsPage;
